// v1.8.0 · CRM · Fase 2: Conversaciones (bandeja del equipo, hilo por contacto, correo/WhatsApp/SMS/llamadas/notas,
// respuestas rápidas con variables y entrada automática del formulario web)
// v1.18.0 · WhatsApp por API: se envía desde aquí (ventana de 24 h), estados entregado/leído y asistente automático.
// v1.25.0 · Imágenes adjuntas (correo y WhatsApp) y respuestas de correo en el mismo hilo («Re: …»).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Star, ArrowLeft, Plus, Send, Zap, RotateCw, Mail, Phone, Trash2, Bot, Paperclip, X } from 'lucide-react';
import { api, errMsg, money, fdate, fdatetime, Modal, inputCls, btn, Field, Empty, Tag } from './ui';
import { ContactPicker } from './Opportunities';

const VIEWS = [['unread', 'No leídas'], ['all', 'Todas'], ['recent', 'Recientes'], ['starred', 'Destacadas']];
export const CHANNELS = {
  email: { label: 'Correo', short: 'Correo', cls: 'bg-blue-50 text-blue-700' },
  whatsapp: { label: 'WhatsApp', short: 'WhatsApp', cls: 'bg-green-50 text-green-700' },
  sms: { label: 'SMS', short: 'SMS', cls: 'bg-violet-50 text-violet-700' },
  call: { label: 'Llamada', short: 'Llamada', cls: 'bg-amber-50 text-amber-800' },
  web: { label: 'Formulario web', short: 'Web', cls: 'bg-cyan-50 text-cyan-800' },
  note: { label: 'Nota interna', short: 'Nota', cls: 'bg-yellow-50 text-yellow-800' },
};
const COMPOSE = ['email', 'whatsapp', 'sms', 'call', 'note'];
const STATUS_TXT = { sent: 'Enviado', failed: 'No enviado', opened: 'Abierto para enviar', received: 'Recibido', logged: 'Registrado', delivered: 'Entregado', read: 'Leído' };
const MEDIA_BASE = process.env.REACT_APP_BACKEND_URL || '';   // v1.25.0
const baseSubject = (s) => (s || '').replace(/^\s*((re|rv|fw|fwd|resp)\s*:\s*)+/i, '').trim();
const VARS = ['nombre', 'apellido', 'nombre_completo', 'empresa', 'correo', 'telefono', 'usuario'];

export const renderVars = (text, contact, userName) => {
  const c = contact || {};
  const v = { nombre: c.first_name || (c.name || '').split(' ')[0], apellido: c.last_name || '', nombre_completo: c.name || '',
              empresa: c.company || '', correo: c.email || '', telefono: c.phone || '', usuario: userName || '' };
  return (text || '').replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (m, k) => (k in v ? v[k] : m));
};

const ago = (iso) => {
  if (!iso) return '';
  const d = (Date.now() - new Date(iso).getTime()) / 60000;
  if (d < 1) return 'ahora';
  if (d < 60) return `${Math.floor(d)} min`;
  if (d < 1440) return `${Math.floor(d / 60)} h`;
  return fdate(iso);
};

function ChannelChip({ ch }) {
  const c = CHANNELS[ch] || CHANNELS.note;
  return <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${c.cls}`}>{c.short}</span>;
}

function SnippetsModal({ onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(async () => { try { setItems((await api.get('/snippets')).items); } catch (e) { toast.error(errMsg(e)); } }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try {
      if (edit.id) await api.put(`/snippets/${edit.id}`, edit); else await api.post('/snippets', edit);
      setEdit(null); load(); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (s) => {
    if (!window.confirm(`¿Eliminar la respuesta «${s.name}»?`)) return;
    try { await api.del(`/snippets/${s.id}`); toast.success('Respuesta eliminada'); load(); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Modal title="Respuestas rápidas" onClose={onClose} wide
           footer={edit ? <><button className={btn.ghost} onClick={() => setEdit(null)}>Volver</button><button className={btn.primary} disabled={!edit.name?.trim() || !edit.body?.trim()} onClick={save}>Guardar</button></>
             : <button className={btn.primary} onClick={() => setEdit({ name: '', body: '', channel: '' })}><Plus size={14} /> Nueva respuesta</button>}>
      {edit ? (
        <div className="space-y-3">
          <Field label="Nombre"><input className={inputCls} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} autoFocus /></Field>
          <Field label="Canal">
            <select className={inputCls} value={edit.channel} onChange={(e) => setEdit({ ...edit, channel: e.target.value })}>
              <option value="">Todos</option>{COMPOSE.map((k) => <option key={k} value={k}>{CHANNELS[k].label}</option>)}
            </select>
          </Field>
          <Field label="Texto" hint={`Variables: ${VARS.map((v) => `{{${v}}}`).join(' ')}`}>
            <textarea className={inputCls} rows={6} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} />
          </Field>
        </div>
      ) : items.length === 0 ? <p className="text-sm text-gray-500">Aún no hay respuestas rápidas. Cree saludos, seguimientos o respuestas frecuentes.</p> : (
        <ul className="divide-y">
          {items.map((s) => (
            <li key={s.id} className="flex items-start gap-3 py-2.5">
              <button className="min-w-0 flex-1 text-left" onClick={() => setEdit(s)}>
                <div className="text-sm font-semibold text-[#0F2A3A]">{s.name} {s.channel && <ChannelChip ch={s.channel} />}</div>
                <div className="truncate text-xs text-gray-500">{s.body}</div>
              </button>
              <button onClick={() => del(s)} aria-label={`Eliminar ${s.name}`} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

function Message({ m, onRetry }) {
  const internal = m.direction === 'internal';
  const out = m.direction === 'out';
  const box = internal ? 'mx-auto max-w-[92%] border border-yellow-200 bg-yellow-50'
    : out ? 'ml-auto max-w-[85%] bg-[#2F3D8E] text-white' : 'mr-auto max-w-[85%] border border-gray-200 bg-white';
  const sub = out ? 'text-white/70' : 'text-gray-500';
  return (
    <div className={`w-fit rounded-2xl px-3.5 py-2.5 text-sm shadow-sm ${box}`} data-testid="conv-message">
      <div className="mb-1 flex flex-wrap items-center gap-1.5">
        <ChannelChip ch={m.channel} />
        {m.subject && <b className={out ? 'text-white' : 'text-[#0F2A3A]'}>{m.subject}</b>}
      </div>
      <div className="whitespace-pre-wrap break-words">{m.body}</div>
      {(m.attachments || []).length > 0 && (   /* v1.25.0 · imágenes adjuntas */
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="conv-attachments">
          {m.attachments.map((a) => (
            <a key={a.id} href={`${MEDIA_BASE}${a.url}`} target="_blank" rel="noreferrer" title={a.filename} className="block">
              <img src={`${MEDIA_BASE}${a.url}`} alt={a.filename || 'Imagen adjunta'} className="h-24 w-24 rounded-lg border border-white/30 object-cover" />
            </a>
          ))}
        </div>
      )}
      <div className={`mt-1 flex flex-wrap items-center gap-x-2 text-[11px] ${sub}`}>
        <span>{m.auto && <Bot size={11} className="mr-0.5 inline" />}{m.channel === 'web' ? 'Formulario web' : m.direction === 'in' ? (m.via === 'whatsapp_api' ? m.by : `Registró ${m.by}`) : m.by}{m.channel === 'call' && m.duration_min ? ` · ${m.duration_min} min` : ''}</span>
        <span>{fdatetime(m.at)}</span>
        {/* v1.8.1 · la llamada dice Entrante/Saliente; el resto, su estado (sin repetir «Recibido») */}
        {m.channel === 'call' ? <span>{m.direction === 'in' ? 'Entrante' : 'Saliente'}</span>
          : m.direction !== 'internal' && m.channel !== 'web' && <span className={m.status === 'failed' ? 'font-semibold text-red-300' : ''}>{STATUS_TXT[m.status] || m.status}</span>}
      </div>
      {m.status === 'failed' && m.channel === 'whatsapp' && <div className="mt-1.5 rounded bg-white/15 px-2 py-1 text-[11px]">{m.error}</div>}
      {m.status === 'failed' && m.channel !== 'whatsapp' && (
        <div className="mt-1.5 rounded bg-white/15 px-2 py-1 text-[11px]">
          {m.error} <button className="ml-1 inline-flex items-center gap-1 font-semibold underline" onClick={() => onRetry(m)}><RotateCw size={11} /> Reintentar</button>
        </div>
      )}
    </div>
  );
}

function Composer({ conv, userName, snippets, onSent, onManageSnippets }) {
  const contact = conv.contact || {};
  const [ch, setCh] = useState(() => (contact.email ? 'email' : contact.phone ? 'whatsapp' : 'note'));
  const [dir, setDir] = useState('out');
  // v1.25.0 · si ya hubo correos, se responde en el mismo hilo: «Re: <asunto>»
  const lastSubject = useMemo(() => [...(conv.messages || [])].reverse().find((m) => m.channel === 'email' && (m.direction === 'in' || m.direction === 'out') && m.subject)?.subject || '', [conv.messages]);
  const replySubject = lastSubject ? `Re: ${baseSubject(lastSubject)}` : '';
  const [subject, setSubject] = useState(replySubject);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);   // v1.25.0 · imágenes subidas para este mensaje
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [duration, setDuration] = useState('');
  const [busy, setBusy] = useState(false);
  const sns = snippets.filter((s) => !s.channel || s.channel === ch);
  const needs = ch === 'email' ? contact.email : (ch === 'whatsapp' || ch === 'sms') ? contact.phone : true;
  const blocked = dir === 'out' && ['email', 'whatsapp', 'sms'].includes(ch) && (contact.dnd || !needs);
  const waApi = ch === 'whatsapp' && dir === 'out' && conv.wa?.api && conv.wa?.window_open && !!conv.wa_id;   // v1.18.0
  const canAttach = (dir === 'out' && (ch === 'email' || waApi)) || ch === 'note';   // v1.25.0
  const attach = async (list) => {
    setUploading(true);
    try {
      for (const f of list.slice(0, 5 - files.length)) {
        if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: supera 10 MB`); continue; }
        const fd = new FormData(); fd.append('file', f);
        const r = await api.post(`/conversations/${conv.id}/attachments`, fd);
        setFiles((x) => [...x, r]);
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const send = async () => {
    if (!body.trim() || busy) return;
    // WhatsApp: se abre la ventana antes de la llamada para que el navegador no la bloquee
    const win = dir === 'out' && ch === 'whatsapp' && !waApi ? window.open('', '_blank') : null;
    setBusy(true);
    try {
      const r = await api.post(`/conversations/${conv.id}/messages`, {
        channel: ch, direction: ch === 'note' ? 'internal' : dir, body, subject: ch === 'email' && dir === 'out' ? subject : '',
        duration_min: ch === 'call' && duration !== '' ? Number(duration) : null,
        attachments: canAttach ? files.map((f) => f.id) : [],
      });
      if (r.link && win) win.location.href = r.link;
      else if (r.link) window.location.href = r.link;
      else if (win) win.close();
      if (r.status === 'failed') toast.error(`${ch === 'whatsapp' ? 'El WhatsApp' : 'El correo'} no se envió: ${r.error}`);
      else toast.success({ sent: ch === 'whatsapp' ? 'WhatsApp enviado' : 'Correo enviado', opened: 'Mensaje listo en ' + CHANNELS[ch].label, received: 'Registrado', logged: 'Registrado' }[r.status] || 'Guardado');
      setBody(''); setSubject(replySubject); setDuration(''); setFiles([]);
      onSent();
    } catch (e) { if (win) win.close(); toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const showDir = ch !== 'note';
  const dirLabels = ch === 'call' ? [['out', 'Saliente'], ['in', 'Entrante']] : [['out', 'Enviar'], ['in', 'Registrar recibido']];

  return (
    <div className="border-t bg-white p-2.5 sm:p-3" data-testid="conv-composer">
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        {/* v1.8.1 · en móvil los canales van en un selector para dejar más alto al hilo */}
        <select className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs font-semibold sm:hidden" value={ch} aria-label="Canal del mensaje"
                onChange={(e) => { setCh(e.target.value); if (e.target.value === 'note') setDir('out'); }}>
          {COMPOSE.map((k) => <option key={k} value={k}>{CHANNELS[k].label}</option>)}
        </select>
        {COMPOSE.map((k) => (
          <button key={k} onClick={() => { setCh(k); if (k === 'note') setDir('out'); }} data-testid={`compose-${k}`}
                  className={`hidden rounded-full px-3 py-1 text-xs font-semibold sm:inline-flex ${ch === k ? 'bg-[#2F3D8E] text-white' : 'border border-gray-300 bg-white text-gray-700'}`}>
            {CHANNELS[k].label}
          </button>
        ))}
        {showDir && (
          <select className="ml-auto rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs" value={dir} onChange={(e) => setDir(e.target.value)} aria-label="Tipo">
            {dirLabels.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        )}
      </div>
      {blocked && (
        <div className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {contact.dnd ? 'El contacto está marcado como «No contactar».' : ch === 'email' ? 'El contacto no tiene correo.' : 'El contacto no tiene teléfono.'} Puede registrar mensajes recibidos o agregar una nota.
        </div>
      )}
      {ch === 'whatsapp' && dir === 'out' && conv.wa?.api && !blocked && (
        <div className={`mb-2 rounded-lg px-3 py-2 text-xs ${waApi ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`}>
          {waApi ? 'Se envía desde el número de la empresa.' : 'Pasaron más de 24 horas desde el último mensaje del cliente: WhatsApp solo deja escribir con plantillas aprobadas. Se abrirá WhatsApp en su teléfono.'}
        </div>
      )}
      {ch === 'email' && dir === 'out' && (
        <input className={`${inputCls} mb-2`} placeholder={`Asunto · para ${contact.email || '—'}`} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="compose-subject" />
      )}
      {ch === 'email' && dir === 'out' && lastSubject && <p className="-mt-1 mb-2 text-[11px] text-gray-500" data-testid="compose-thread">Responde en el mismo hilo del correo «{baseSubject(lastSubject)}».</p>}
      <textarea className={`${inputCls} min-h-[52px] sm:min-h-[76px]`} rows={2} value={body} onChange={(e) => setBody(e.target.value)} data-testid="compose-body"
                placeholder={ch === 'note' ? 'Nota visible solo para el equipo' : ch === 'call' ? 'Resumen de la llamada' : dir === 'in' ? 'Pegue o resuma lo que escribió el contacto' : 'Escriba su mensaje · Ctrl+Enter para enviar'}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); } }} />
      {canAttach && files.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="compose-files">
          {files.map((f) => (
            <span key={f.id} className="relative">
              <img src={`${MEDIA_BASE}${f.url}`} alt={f.filename} className="h-14 w-14 rounded-md border object-cover" />
              <button type="button" onClick={() => setFiles((x) => x.filter((y) => y.id !== f.id))} aria-label={`Quitar ${f.filename}`}
                      className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 shadow"><X size={11} /></button>
            </span>
          ))}
        </div>
      )}
      {ch === 'whatsapp' && dir === 'out' && !waApi && !blocked && <p className="mt-1 text-[11px] text-gray-500">Para adjuntar imágenes por WhatsApp desde el CRM, el cliente debe haber escrito en las últimas 24 horas.</p>}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs" value="" aria-label="Insertar respuesta rápida"
                onChange={(e) => { const s = sns.find((x) => x.id === e.target.value); if (s) setBody((b) => (b ? `${b}\n` : '') + renderVars(s.body, contact, userName)); }}>
          <option value="">Respuesta rápida…</option>
          {sns.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <button className="inline-flex items-center gap-1 text-xs font-semibold text-[#2F3D8E] hover:underline" onClick={onManageSnippets}><Zap size={12} /> Administrar</button>
        {canAttach && (<>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple className="hidden" onChange={(e) => attach([...e.target.files])} data-testid="compose-file" />
          <button type="button" className="inline-flex items-center gap-1 text-xs font-semibold text-[#2F3D8E] hover:underline disabled:opacity-50" disabled={uploading || files.length >= 5}
                  onClick={() => fileRef.current?.click()} data-testid="compose-attach"><Paperclip size={12} /> {uploading ? 'Subiendo…' : 'Adjuntar imagen'}</button>
        </>)}
        {ch === 'call' && <input type="number" min="0" className="w-24 rounded-lg border border-gray-300 px-2 py-1.5 text-xs" placeholder="Minutos" value={duration} onChange={(e) => setDuration(e.target.value)} aria-label="Duración en minutos" />}
        <button className={`${btn.primary} ml-auto`} disabled={!body.trim() || busy || uploading || blocked || (ch === 'email' && dir === 'out' && !subject.trim() && !lastSubject)} onClick={send} data-testid="compose-send">
          <Send size={14} /> {busy ? 'Enviando…' : ch === 'note' ? 'Agregar nota' : dir === 'in' || ch === 'call' ? 'Registrar' : ch === 'email' ? 'Enviar correo' : waApi ? 'Enviar por WhatsApp' : `Abrir ${CHANNELS[ch].label}`}
        </button>
      </div>
    </div>
  );
}

function Thread({ meta, convId, userName, snippets, onBack, onChanged, onManageSnippets }) {
  const [conv, setConv] = useState(null);
  const endRef = useRef(null);
  const [showActions, setShowActions] = useState(false);   // v1.8.1 · móvil: acciones plegadas
  const changedRef = useRef(onChanged);
  changedRef.current = onChanged;
  const load = useCallback(async (opening = false) => {
    try {
      const r = await api.get(`/conversations/${convId}`);
      setConv(r);
      if (opening) changedRef.current();   // abrirla la marca como leída: refrescar bandeja y contador
    } catch (e) { toast.error(errMsg(e)); }
  }, [convId]);
  useEffect(() => { setConv(null); load(true); }, [load]);
  const nMessages = conv?.messages?.length;
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [nMessages]);

  if (!conv) return <div className="flex flex-1 items-center justify-center text-sm text-gray-500">Cargando conversación…</div>;
  const patch = async (body) => {
    try { const r = await api.patch(`/conversations/${conv.id}`, body); setConv({ ...conv, ...r }); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const retry = async (m) => {
    try { const r = await api.post(`/messages/${m.id}/retry`, {}); if (r.status === 'sent') toast.success('Correo enviado'); else toast.error(`Sigue sin enviarse: ${r.error}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm('¿Eliminar toda la conversación? Se borran sus mensajes. El contacto se conserva.')) return;
    try { await api.del(`/conversations/${conv.id}`); toast.success('Conversación eliminada'); onBack(true); } catch (e) { toast.error(errMsg(e)); }
  };
  const c = conv.contact || {};
  const wa = conv.wa;   // v1.18.0
  const setBot = async (active) => {
    try { const st = await api.post(`/whatsapp/conversations/${conv.id}/bot`, { active }); setConv({ ...conv, wa: st }); toast.success(active ? 'Asistente reactivado' : 'Asistente pausado'); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-testid="conv-thread">
      <div className="flex flex-wrap items-center gap-2 border-b bg-white px-3 py-2">
        <button className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-100 lg:hidden" onClick={() => onBack(false)} aria-label="Volver a la bandeja"><ArrowLeft size={16} /></button>
        <div className="min-w-0 flex-1 basis-[calc(100%-48px)] sm:basis-0">
          <Link to={`/crm/contactos/${conv.contact_id}`} className="block truncate text-[15px] font-bold text-[#0F2A3A] hover:underline">{conv.contact_name}</Link>
          <div className="flex min-w-0 flex-wrap gap-x-3 text-xs text-gray-500">
            {c.email && <span className="inline-flex min-w-0 max-w-full items-center gap-1"><Mail size={11} className="shrink-0" /><span className="truncate">{c.email}</span></span>}
            {c.phone && <span className="inline-flex items-center gap-1"><Phone size={11} />{c.phone}</span>}
            {c.dnd && <span className="font-semibold text-amber-700">No contactar</span>}
          </div>
        </div>
        <button className="rounded-lg border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700 sm:hidden" onClick={() => setShowActions((v) => !v)} aria-expanded={showActions}>
          Acciones
        </button>
        <div className={`${showActions ? 'flex' : 'hidden'} w-full flex-wrap items-center gap-2 sm:ml-auto sm:flex sm:w-auto`}>
        {meta.scope !== 'own' && (   // v1.12.1 · con «solo sus registros» no se reasigna
        <select className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs" value={conv.assigned_to || ''} onChange={(e) => patch({ assigned_to: e.target.value })} aria-label="Asignar">
          <option value="">Sin asignar</option>{meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        )}
        <button className={`rounded-lg border px-2 py-1.5 ${conv.starred ? 'border-amber-300 bg-amber-50 text-amber-600' : 'border-gray-300 text-gray-500'}`} onClick={() => patch({ starred: !conv.starred })} aria-label={conv.starred ? 'Quitar destacado' : 'Destacar'} aria-pressed={conv.starred}><Star size={14} /></button>
        <button className={btn.ghost} onClick={() => patch({ unread: true })}>No leída</button>
        <button className={btn.ghost} onClick={() => patch({ status: conv.status === 'open' ? 'closed' : 'open' })}>{conv.status === 'open' ? 'Cerrar' : 'Reabrir'}</button>
        {meta.is_admin && <button className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600" onClick={remove} aria-label="Eliminar conversación"><Trash2 size={14} /></button>}
        </div>
      </div>
      {conv.open_opportunities?.length > 0 && (
        <div className="hidden flex-wrap gap-1.5 border-b sm:flex bg-gray-50 px-3 py-1.5 text-xs">
          <span className="text-gray-500">Oportunidades abiertas:</span>
          {conv.open_opportunities.map((o) => <span key={o.id} className="rounded-full bg-white px-2 py-0.5 font-medium text-gray-700 ring-1 ring-gray-200">{o.name} · {money(o.value)}</span>)}
        </div>
      )}
      {wa?.api && wa.auto && conv.wa_id && (
        <div className={`flex flex-wrap items-center gap-2 border-b px-3 py-1.5 text-xs ${wa.active ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`} data-testid="wa-bot-banner">
          <Bot size={13} />
          <span className="min-w-0 flex-1">{wa.active ? 'El asistente responde automáticamente en esta conversación.'
            : wa.handoff ? 'El asistente pasó esta conversación a una persona y dejó de responder.'
            : wa.paused_until && new Date(wa.paused_until) - Date.now() < 300 * 864e5 ? `Asistente en pausa hasta ${fdatetime(wa.paused_until)} (alguien del equipo escribió).` : 'Asistente en pausa en esta conversación.'}</span>
          <button className="rounded-md border border-current px-2 py-0.5 font-semibold" onClick={() => setBot(!wa.active)} data-testid="wa-bot-toggle">{wa.active ? 'Pausar' : 'Reactivar'}</button>
        </div>
      )}
      <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto bg-gray-50 px-3 py-4">
        {conv.messages.length === 0 && <p className="py-10 text-center text-sm text-gray-500">Aún no hay mensajes. Escriba el primero abajo.</p>}
        {conv.messages.map((m) => <Message key={m.id} m={m} onRetry={retry} />)}
        <div ref={endRef} />
      </div>
      <Composer key={conv.id} conv={conv} userName={userName} snippets={snippets} onSent={() => { load(); onChanged(); }} onManageSnippets={onManageSnippets} />
    </div>
  );
}

export default function CrmConversations({ meta, onCounts }) {
  const [params, setParams] = useSearchParams();
  const selected = params.get('c');
  const [view, setView] = useState('all');
  const [status, setStatus] = useState('open');
  const [assigned, setAssigned] = useState('');
  const [channel, setChannel] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [list, setList] = useState({ items: [], total: 0 });
  const [snippets, setSnippets] = useState([]);
  const [modal, setModal] = useState(null);
  const users = useMemo(() => Object.fromEntries(meta.users.map((u) => [u.id, u.name])), [meta.users]);
  const userName = users[meta.me] || '';

  useEffect(() => { const t = setTimeout(() => setQ(qInput), 300); return () => clearTimeout(t); }, [qInput]);
  // v1.8.1 · solo se aplica la respuesta de la última petición (antes una respuesta tardía pisaba el filtro nuevo)
  const reqRef = useRef(0);
  const load = useCallback(async () => {
    const n = ++reqRef.current;
    try {
      const r = await api.get('/conversations', { view, status: status === 'all' ? '' : status, assigned_to: assigned, channel, q });
      if (n === reqRef.current) setList(r);
    } catch (e) { if (n === reqRef.current) toast.error(errMsg(e)); }
  }, [view, status, assigned, channel, q]);
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [load]);
  const loadSnippets = useCallback(async () => { try { setSnippets((await api.get('/snippets')).items); } catch { setSnippets([]); } }, []);
  useEffect(() => { loadSnippets(); }, [loadSnippets]);

  const select = (id) => setParams(id ? { tab: 'conversations', c: id } : { tab: 'conversations' });
  const changed = () => { load(); onCounts(); };
  const startNew = async (contactId) => {
    try { const c = await api.post('/conversations', { contact_id: contactId }); setModal(null); select(c.id); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const pill = (on) => `rounded-full px-3 py-1 text-xs font-semibold ${on ? 'bg-[#2F3D8E] text-white' : 'border border-gray-300 bg-white text-gray-700'}`;

  return (
    <div className="flex h-[calc(100dvh-150px)] min-h-[460px] overflow-hidden sm:h-[calc(100vh-190px)] sm:min-h-[520px] rounded-xl border border-gray-200 bg-white" data-testid="crm-conversations">
      <aside className={`${selected ? 'hidden lg:flex' : 'flex'} w-full min-w-0 flex-col border-r lg:w-[360px] lg:shrink-0`}>
        <div className="space-y-2 border-b p-3">
          <div className="flex gap-2">
            <div className="relative min-w-0 flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input className={`${inputCls} pl-8`} placeholder="Buscar contacto o mensaje" value={qInput} onChange={(e) => setQInput(e.target.value)} data-testid="conv-search" />
            </div>
            <button className={btn.primary} onClick={() => setModal('new')} data-testid="conv-new" aria-label="Nueva conversación"><Plus size={15} /></button>
          </div>
          <div className="flex flex-wrap gap-1.5">{VIEWS.map(([v, l]) => <button key={v} className={pill(view === v)} onClick={() => { setView(v); if (v === 'starred') setStatus('all'); }}>{l}</button>)}</div>
          <div className="grid grid-cols-3 gap-1.5">
            <select className="min-w-0 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Estado">
              <option value="open">Abiertas</option><option value="closed">Cerradas</option><option value="all">Todas</option>
            </select>
            <select className="min-w-0 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs" value={assigned} onChange={(e) => setAssigned(e.target.value)} aria-label="Asignadas a">
              <option value="">Equipo</option>{meta.me && <option value={meta.me}>Mías</option>}<option value="unassigned">Sin asignar</option>
              {meta.users.filter((u) => u.id !== meta.me).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <select className="min-w-0 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs" value={channel} onChange={(e) => setChannel(e.target.value)} aria-label="Canal">
              <option value="">Todo canal</option>{Object.entries(CHANNELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>
        <ul className="min-h-0 flex-1 divide-y overflow-y-auto">
          {list.items.length === 0 && <li className="px-4 py-10 text-center text-sm text-gray-500">{view === 'unread' ? 'No hay conversaciones sin leer.' : 'No hay conversaciones con estos filtros.'}</li>}
          {list.items.map((c) => (
            <li key={c.id}>
              <button onClick={() => select(c.id)} data-testid="conv-item"
                      className={`flex w-full gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 ${selected === c.id ? 'bg-[#2F3D8E]/5' : ''}`}>
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${c.unread ? 'bg-[#2a78d6]' : 'bg-transparent'}`} aria-label={c.unread ? 'No leída' : undefined} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className={`truncate text-sm ${c.unread ? 'font-bold text-[#0F2A3A]' : 'font-medium text-gray-800'}`}>{c.contact_name}</span>
                    {c.starred && <Star size={11} className="shrink-0 text-amber-500" />}
                    <span className="ml-auto shrink-0 text-[11px] text-gray-500">{ago(c.last_message_at)}</span>
                  </span>
                  <span className="mt-0.5 flex items-center gap-1.5">
                    {c.last_channel && <ChannelChip ch={c.last_channel} />}
                    <span className="truncate text-xs text-gray-500">{c.last_direction === 'out' ? 'Usted: ' : ''}{c.last_message_preview || 'Sin mensajes'}</span>
                  </span>
                  {c.assigned_to && <span className="mt-0.5 block text-[11px] text-gray-400">{users[c.assigned_to] || ''}</span>}
                  {c.status === 'closed' && <Tag>cerrada</Tag>}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <div className="border-t px-3 py-1.5 text-[11px] text-gray-500">{list.total} conversaciones</div>
      </aside>
      <section className={`${selected ? 'flex' : 'hidden lg:flex'} min-w-0 flex-1 flex-col`}>
        {selected ? (
          <Thread meta={meta} convId={selected} userName={userName} snippets={snippets} onChanged={changed} onManageSnippets={() => setModal('snippets')}
                  onBack={(reload) => { select(null); if (reload) changed(); }} />
        ) : (
          <div className="flex flex-1 items-center justify-center p-6">
            <Empty title="Elija una conversación" action={<button className={btn.primary} onClick={() => setModal('new')}><Plus size={14} /> Nueva conversación</button>}>
              Aquí llegan las solicitudes del formulario web y el historial de correos, WhatsApp, SMS, llamadas y notas con cada contacto.
            </Empty>
          </div>
        )}
      </section>
      {modal === 'new' && (
        <Modal title="Nueva conversación" onClose={() => setModal(null)}>
          <Field label="Contacto" group><ContactPicker value={null} label="" onChange={(id) => id && startNew(id)} /></Field>
        </Modal>
      )}
      {modal === 'snippets' && <SnippetsModal onClose={() => setModal(null)} onChanged={loadSnippets} />}
    </div>
  );
}
